import type { LayoutInput, LayoutResult, TreeOptions, LayoutNode } from '../types';
import { TREE_DEFAULTS } from '../defaults';
import { shapeBounds } from '../utils/geometry';

/**
 * Hierarchical tree layout.
 *
 * Builds a tree from edges and arranges nodes level-by-level.
 * Direction controls the primary axis of expansion.
 */
export function tree(
	input: LayoutInput,
	options?: Partial<TreeOptions>,
): LayoutResult {
	const opts: TreeOptions = { ...TREE_DEFAULTS, ...options };
	const { nodes, edges } = input;

	if (nodes.length === 0) return { nodes: [] };
	if (nodes.length === 1) {
		const b = shapeBounds(nodes[0].shape);
		return { nodes: [{ id: nodes[0].id, x: b.x, y: b.y }] };
	}

	const nodeMap = new Map(nodes.map((n) => [n.id, n]));

	// Build adjacency: parent -> children
	const children = new Map<string, string[]>();
	const incomingCount = new Map<string, number>();

	for (const n of nodes) {
		children.set(n.id, []);
		incomingCount.set(n.id, 0);
	}

	for (const edge of edges) {
		if (!nodeMap.has(edge.source) || !nodeMap.has(edge.target)) continue;
		children.get(edge.source)!.push(edge.target);
		incomingCount.set(edge.target, (incomingCount.get(edge.target) ?? 0) + 1);
	}

	// Find root
	let rootId: string;
	if (opts.root !== 'auto') {
		rootId = opts.root;
	} else {
		// Node with no incoming edges
		rootId = nodes[0].id;
		for (const n of nodes) {
			if ((incomingCount.get(n.id) ?? 0) === 0) {
				rootId = n.id;
				break;
			}
		}
	}

	// BFS to assign levels and handle multi-parent
	const level = new Map<string, number>();
	const parent = new Map<string, string>();
	const assigned = new Set<string>();
	const queue: string[] = [rootId];
	level.set(rootId, 0);
	assigned.add(rootId);

	// Track actual tree children (after multi-parent resolution)
	const treeChildren = new Map<string, string[]>();
	for (const n of nodes) treeChildren.set(n.id, []);

	while (queue.length > 0) {
		const current = queue.shift()!;
		const currentLevel = level.get(current)!;

		for (const childId of children.get(current) ?? []) {
			if (assigned.has(childId)) {
				// Multi-parent handling
				if (opts.multiParent === 'closest') {
					const existingLevel = level.get(childId)!;
					if (currentLevel + 1 < existingLevel) {
						// Re-assign to closer parent
						const oldParent = parent.get(childId);
						if (oldParent) {
							const oldChildren = treeChildren.get(oldParent)!;
							const idx = oldChildren.indexOf(childId);
							if (idx >= 0) oldChildren.splice(idx, 1);
						}
						level.set(childId, currentLevel + 1);
						parent.set(childId, current);
						treeChildren.get(current)!.push(childId);
					}
				}
				// 'first' and 'duplicate' — skip (first assignment wins)
				continue;
			}

			assigned.add(childId);
			level.set(childId, currentLevel + 1);
			parent.set(childId, current);
			treeChildren.get(current)!.push(childId);
			queue.push(childId);
		}
	}

	// Include orphan nodes (not reachable from root) at level 0
	for (const n of nodes) {
		if (!assigned.has(n.id)) {
			assigned.add(n.id);
			level.set(n.id, 0);
			treeChildren.get(rootId)!.push(n.id);
		}
	}

	// Compute subtree widths (in cross-axis dimension)
	const subtreeWidth = new Map<string, number>();

	function computeSubtreeWidth(nodeId: string): number {
		const childIds = treeChildren.get(nodeId) ?? [];
		const b = shapeBounds(nodeMap.get(nodeId)!.shape);
		const isHorizontal = opts.direction === 'right' || opts.direction === 'left';
		const nodeWidth = isHorizontal ? b.height : b.width;

		if (childIds.length === 0) {
			subtreeWidth.set(nodeId, nodeWidth);
			return nodeWidth;
		}

		let totalChildWidth = 0;
		for (const childId of childIds) {
			totalChildWidth += computeSubtreeWidth(childId);
		}
		totalChildWidth += (childIds.length - 1) * opts.spacing.sibling;

		const width = Math.max(nodeWidth, totalChildWidth);
		subtreeWidth.set(nodeId, width);
		return width;
	}

	computeSubtreeWidth(rootId);

	// Position nodes
	const positions = new Map<string, { x: number; y: number }>();
	const isHorizontal = opts.direction === 'right' || opts.direction === 'left';
	const isReversed = opts.direction === 'up' || opts.direction === 'left';

	function positionSubtree(
		nodeId: string,
		crossStart: number,
		levelDepth: number,
	): void {
		const node = nodeMap.get(nodeId)!;
		const b = shapeBounds(node.shape);
		const nodeSize = isHorizontal ? b.height : b.width;
		const nodeLevelSize = isHorizontal ? b.width : b.height;
		const sw = subtreeWidth.get(nodeId)!;

		// Position this node centered in its subtree
		let crossPos: number;
		if (opts.childAlign === 'start') {
			crossPos = crossStart;
		} else if (opts.childAlign === 'end') {
			crossPos = crossStart + sw - nodeSize;
		} else {
			crossPos = crossStart + (sw - nodeSize) / 2;
		}

		const levelPos = levelDepth * (isReversed ? -1 : 1);

		if (isHorizontal) {
			positions.set(nodeId, { x: levelPos, y: crossPos });
		} else {
			positions.set(nodeId, { x: crossPos, y: levelPos });
		}

		// Position children
		const childIds = treeChildren.get(nodeId) ?? [];
		if (childIds.length === 0) return;

		const childrenTotalWidth = childIds.reduce(
			(sum, cid) => sum + (subtreeWidth.get(cid) ?? 0),
			0,
		) + (childIds.length - 1) * opts.spacing.sibling;

		let childCross = crossStart + (sw - childrenTotalWidth) / 2;
		const childLevel = levelDepth + nodeLevelSize + opts.spacing.level;

		for (const childId of childIds) {
			positionSubtree(childId, childCross, childLevel);
			childCross += (subtreeWidth.get(childId) ?? 0) + opts.spacing.sibling;
		}
	}

	positionSubtree(rootId, 0, 0);

	// Apply: offset so root is at its original position
	const rootBounds = shapeBounds(nodeMap.get(rootId)!.shape);
	const rootPos = positions.get(rootId)!;
	const offsetX = rootBounds.x - rootPos.x;
	const offsetY = rootBounds.y - rootPos.y;

	return {
		nodes: nodes.map((n) => {
			const pos = positions.get(n.id);
			if (!pos) {
				const b = shapeBounds(n.shape);
				return { id: n.id, x: b.x, y: b.y };
			}
			return {
				id: n.id,
				x: pos.x + offsetX,
				y: pos.y + offsetY,
			};
		}),
	};
}
